import React from 'react';
import PropTypes from 'prop-types';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatDateTime } from 'helpers/formatters/date_time_formats';
import { every } from 'lodash';
import { withRouter } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck } from '@fortawesome/pro-solid-svg-icons';

import { fetchShareableTokens, bulkUpdateShareableTokens } from 'components/project/api';

export class ShareModal extends React.Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    projectId: PropTypes.number.isRequired,
    resendToken: PropTypes.func,
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired
  };

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.setState({ shareableTokens: null, changes: null, confirming: false, saving: false });
      fetchShareableTokens(this.props.projectId).then(({ shareableTokens }) => {
        const copyOfShareableTokens = shareableTokens.map(t => ({ ...t }));
        this.setState({ shareableTokens, changes: copyOfShareableTokens, loading: false });
      });
    }
  }

  state = {
    confirming: false,
    resending: false,
    saving: false
  }

  saveChanges = () => {
    this.setState({ saving: true });
    bulkUpdateShareableTokens(
      this.props.projectId,
      this.state.changes.map(({ id, enabled }) => ({ id, enabled }))
    ).then(this.props.onHide);
  }

  removedTokens() {
    const tokens = [];
    if (!this.state.changes) return tokens;

    this.state.changes.forEach((token, i) => {
      if (!token.enabled && this.state.shareableTokens[i].enabled) {
        tokens.push(token);
      }
    });

    return tokens;
  }

  addedTokens() {
    const tokens = [];
    if (!this.state.changes) return tokens;

    this.state.changes.forEach((token, i) => {
      if (token.enabled && !this.state.shareableTokens[i].enabled) {
        tokens.push(token);
      }
    });

    return tokens;
  }

  toggleEnabled = (id) => {
    this.setState((prevState) => {
      const updatedTokens = prevState.changes.map((token) => {
        if (token.id !== id) return { ...token };

        return { ...token, enabled: !!token.email && !token.enabled };
      });

      return { changes: updatedTokens };
    });
  }

  resend = (e, shareableToken) => {
    e.preventDefault();
    this.setState({ resending: true });

    this.props.resendToken(shareableToken.id).then(() => {
      this.setState((prevState) => {
        const updatedTokens = prevState.changes.map((token) => {
          if (token.id !== shareableToken.id) return { ...token };

          return { ...token, resent: true };
        });

        return { changes: updatedTokens, resending: false };
      });
    });
  }

  transitionToOverviewTab = (e) => {
    e.preventDefault();
    this.setState({ shouldMoveToOverviewTab: true });
    this.props.onHide();
  }

  toggleAllEnabled = () => {
    if (this.allTokensEnabled()) {
      this.state.changes.forEach(({ id }) => this.toggleEnabled(id));
    } else {
      this.state.changes.forEach(({ id, enabled }) => {
        if (!enabled) this.toggleEnabled(id);
      });
    }
  }

  someTokensEnabled = () => !this.allTokensEnabled() && this.state.changes.some(t => t.enabled)

  allTokensEnabled = () => every(this.state.changes, t => !t.email || t.enabled)

  moveToConfirmed = () => {
    this.setState({ confirming: true });
  }

  renderWithTooltip = (button, tooltipText, placement) => {
    const tooltip = (
      <Tooltip id="disable-save-button">
        {tooltipText}
      </Tooltip>
    );

    return (
      <OverlayTrigger overlay={tooltip} placement={placement}>
        <span className="d-inline-block">
          {button}
        </span>
      </OverlayTrigger>
    );
  }

  renderLastSentTimestamp(time) {
    if (time) return <span name="formatted-date">{formatDateTime(time)}</span>;
  }

  renderProjectDetailsLink(linkName) {
    return <a href="#" name="overview-tab-link" onClick={this.transitionToOverviewTab}> {linkName} </a>;
  }

  renderSave() {
    const disabled = this.addedTokens().length === 0 && this.removedTokens().length === 0;

    const button = (
      <button
        className="btn btn-primary"
        disabled={disabled}
        name="next-step"
        style={disabled ? { pointerEvents: 'none' } : {}}
        onClick={this.moveToConfirmed}
      >
        Next
      </button>
    );

    return disabled ? this.renderWithTooltip(button, "No changes made", "top") : button;
  }

  renderCheckboxInput = (shareableToken) => {
    const disabled = !shareableToken.email;

    const checkbox = (
      <input
        checked={shareableToken.enabled}
        disabled={disabled}
        name="enabled"
        style={disabled ? { pointerEvents: 'none' } : {}}
        type="checkbox"
        onChange={() => this.toggleEnabled(shareableToken.id)}
      />
    );

    return disabled ? this.renderWithTooltip(checkbox, "This contact doesn't have an email", "bottom") : checkbox;
  }

  canResend = (shareableToken) => {
    const savedToken = this.state.shareableTokens.find(token => token.id == shareableToken.id);
    return savedToken && savedToken.enabled && shareableToken.enabled;
  }

  renderEmailColumn = (shareableToken) => {
    return shareableToken.email ? (
      <span>{shareableToken.email}</span>
    ) : (
      <span className="text-danger">Please add email to contact</span>
    );
  }

  renderResendLink = (shareableToken) => {
    if (!this.canResend(shareableToken)) return;

    if (shareableToken.resent) {
      return (
        <span className="text-success ml-4 d-flex" name="resent-message">
          <FontAwesomeIcon className="mr-1" icon={faCheck}/>
          Sent!
        </span>
      );
    }

    return (
      <a
        className="clickable btn-link ml-4"
        disabled={this.state.resending}
        name="resend-link"
        onClick={e => this.resend(e, shareableToken)}
      >
        Resend
      </a>
    );
  }

  renderShareableToken = (shareableToken) => {
    return (
      <tr key={shareableToken.name} name="token-row">
        <td>
          {this.renderCheckboxInput(shareableToken)}
        </td>
        <td>{shareableToken.name}</td>
        <td>
          {this.renderEmailColumn(shareableToken)}
        </td>
        <td>{shareableToken.relationship}</td>
        <td>
          <span className="text-muted">
            {this.renderLastSentTimestamp(shareableToken.lastSent)}
          </span>
        </td>
        <td>
          {this.renderResendLink(shareableToken)}
        </td>
      </tr>
    );
  }

  renderShareableTokens = () => {
    if (!this.state.changes) return;

    return this.state.changes.map(shareableToken => this.renderShareableToken(shareableToken));
  }

  renderEmptyState() {
    return (
      <p>
        This Client Report can be shared with your Project Contacts.
        To share the Client Report, add Contacts to your Project in the
        {this.renderProjectDetailsLink('Details section.')}
      </p>
    );
  }

  renderBody() {
    if (!this.state.changes) {
      return (
        <div className="text-center py-4" name="loading">
          <FontAwesomeIcon className="fa-spin" icon={faSpinner} size="lg" />
        </div>
      );
    }

    if (this.state.changes.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <div>
        <p>
          Select which Contacts should have access to this Client Report.
          If a Contact is not listed here, you must first
          {this.renderProjectDetailsLink('add them')} on the Details card in the Overview tab.
        </p>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    checked={this.allTokensEnabled()}
                    name="toggle-all"
                    ref={el => el && (el.indeterminate = this.someTokensEnabled())}
                    type="checkbox"
                    onChange={() => this.toggleAllEnabled()}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Relation</th>
                <th>Last Sent</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {this.renderShareableTokens()}
            </tbody>
          </table>
        </div>
        <div className="text-right">
          {this.renderSave()}
        </div>
      </div>
    );
  }

  renderConfirmationToken = (token) => {
    return (
      <tr key={token.id}>
        <td>{token.name}</td>
        <td>{token.email}</td>
      </tr>
    );
  }

  renderModalTitle() {
    if (this.state.confirming) return "Confirm Changes";

    return "Share Report";
  }

  renderConfirmation() {
    const added = this.addedTokens();
    const removed = this.removedTokens();

    let confirmButtonContent = 'Confirm';
    if (this.state.saving) {
      confirmButtonContent = (
        <React.Fragment>
          <FontAwesomeIcon className="fa-spin mr-2" icon={faSpinner} />
          Saving
        </React.Fragment>
      );
    }

    return (
      <div>
        {added.length > 0 &&
          <div>
            <h5>Give access to:</h5>
            <p>We will send an email with an access link to these users:</p>
            <table className="table table-condensed" name="tokens-to-enable">
              <tbody>
                {added.map(this.renderConfirmationToken)}
              </tbody>
            </table>
          </div>
        }
        {removed.length > 0 &&
          <div>
            <h5>Revoke access:</h5>
            <p>We will disable access for these users:</p>
            <table className="table table-condensed" name="tokens-to-disable">
              <tbody>
                {removed.map(this.renderConfirmationToken)}
              </tbody>
            </table>
          </div>
        }
        <div className="mt-3 text-right">
          <button className="btn btn-cancel mr-2" onClick={() => this.setState({ confirming: false })}>Back</button>
          <button
            className="btn btn-save"
            disabled={this.state.saving}
            name="confirm"
            onClick={this.saveChanges}
          >
            {confirmButtonContent}
          </button>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Modal
        bsSize="lg"
        dialogClassName="responsive"
        show={this.props.show}
        onExited={this.state.shouldMoveToOverviewTab ? this.props.history.push('/overview') : null}
        onHide={this.props.onHide}
      >
        <Modal.Header closeButton>
          <Modal.Title>{this.renderModalTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.confirming ? this.renderConfirmation() : this.renderBody()}
        </Modal.Body>
      </Modal>
    );
  }
}

export default withRouter(ShareModal);
